import React, { useState } from "react";
import { Button, Menu, MenuItem, Box } from "@mui/material";
// Iconsax (2026-09-04, matching the AI Agents pass) -- Calendar is the same
// icon DateLabel.jsx's own popup already uses for its "reports" variant, so
// the trigger button and the popup it opens now share one icon instead of
// two different calendar glyphs.
import { Calendar } from "iconsax-react";
import DateLabel from "../Dashboard/Features/DateLabel";
import { reportsFilterPillSx } from "./reportsFilterStyles";

// Extracted out of ReportsTable.jsx so the SAME date-range control (state,
// menu, custom-range popover) can render in two different places -- the
// main filter bar (Overview mode) or the category drill-down header
// (CategoryReportDetail) -- without duplicating it. Only one of those spots
// renders it at a time (ReportsTable conditionally picks which), so there's
// never two live instances. `startDate`/`endDate` stay lifted in
// ReportsTable (the real source of truth, apply-gated same as before);
// this component only stages new dates via onRangeChange, it doesn't fetch.
// `maxRangeDays` -- optional, passed straight through to DateLabel's custom
// range dialog (2026-08-15, "Filter by Count" grouped mode needs a much
// tighter cap than the 365-day default every other Reports date picker
// uses). Undefined here means "use DateLabel's own default."
const ReportsDateRangeControl = ({ startDate, endDate, onRangeChange, maxRangeDays }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDateLabel, setOpenDateLabel] = useState(false);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleMenuItemClick = (range) => {
    handleClose();
    const today = new Date();
    let newStartDate;
    let newEndDate;

    switch (range) {
      case "Today":
        newStartDate = new Date(today.setHours(0, 0, 0, 0));
        newEndDate = new Date(today.setHours(23, 59, 59, 999));
        break;
      case "Week": {
        const firstDayOfWeek = new Date(today);
        const lastDayOfWeek = new Date(today);
        firstDayOfWeek.setDate(today.getDate() - today.getDay());
        firstDayOfWeek.setHours(0, 0, 0, 0);
        lastDayOfWeek.setDate(today.getDate() + (6 - today.getDay()));
        lastDayOfWeek.setHours(23, 59, 59, 999);
        newStartDate = firstDayOfWeek;
        newEndDate = lastDayOfWeek;
        break;
      }
      case "Month":
        newStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
        newEndDate = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
        break;
      case "Custom Range":
        setOpenDateLabel(true);
        return;
      default:
        return;
    }

    onRangeChange(newStartDate, newEndDate);
  };

  const handleCustomDateRange = (customStartDate, customEndDate) => {
    onRangeChange(new Date(customStartDate), new Date(customEndDate));
    setOpenDateLabel(false);
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleClick}
        startIcon={<Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}><Calendar size="100%" color="currentColor" /></Box>}
        size="small"
        sx={reportsFilterPillSx}
      >
        {startDate && endDate
          ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
          : "Select Date Range"}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              borderRadius: "16px",
              border: (theme) => `1px solid ${theme.palette.divider}`,
              boxShadow: (theme) => (theme.palette.mode === "dark" ? "0 8px 24px rgba(0,0,0,0.4)" : "0 8px 24px rgba(0,0,0,0.12)"),
              py: 0.5,
            },
          },
        }}
      >
        {[
          { label: "Today", value: "Today" },
          { label: "This Week", value: "Week" },
          { label: "This Month", value: "Month" },
          { label: "Custom Range", value: "Custom Range" },
        ].map((item) => (
          <MenuItem
            key={item.value}
            onClick={() => handleMenuItemClick(item.value)}
            sx={{
              fontSize: "0.9rem",
              py: 0.95,
              px: 1.625,
              mb: 0.25,
              borderRadius: "8px",
              mx: 0.5,
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      {openDateLabel && (
        <DateLabel
          startDateProp={startDate}
          endDateProp={endDate}
          onClose={() => setOpenDateLabel(false)}
          onDateChange={handleCustomDateRange}
          variant="reports"
          {...(maxRangeDays !== undefined ? { maxRangeDays } : {})}
        />
      )}
    </>
  );
};

export default ReportsDateRangeControl;
